import React from 'react';

/**
 * ChunkErrorBoundary — final safety net. If `lazyWithRetry` already
 * hard-reloaded once and the fresh load STILL fails (e.g. CDN mid-deploy,
 * offline, or a genuine app bug), we show a friendly reload prompt
 * instead of a blank white screen.
 */
export class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunk: false };
  }

  static getDerivedStateFromError(err) {
    const msg = String((err && err.message) || err || '');
    const isChunk =
      (err && err.name === 'ChunkLoadError') ||
      /Loading (chunk|CSS chunk) [\d\w]+ failed/i.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /Unexpected token ['"]?<['"]?/i.test(msg);
    return { hasError: true, isChunk };
  }

  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error('[ChunkErrorBoundary]', err, info);
  }

  handleReload = () => {
    try { window.sessionStorage.removeItem('chunk_reload_attempted'); } catch { /* ignore */ }
    try { window.sessionStorage.removeItem('chunk_reload_attempted_root'); } catch { /* ignore */ }
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const { isChunk } = this.state;
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="chunk-error-boundary">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl shadow-sm p-6 text-center">
          <div className="text-4xl mb-3">⟳</div>
          <h1 className="text-lg font-bold text-slate-800 mb-2">
            {isChunk ? 'A newer version is available' : 'Something went wrong'}
          </h1>
          <p className="text-sm text-slate-500 mb-5">
            {isChunk
              ? 'Please reload to pick up the latest update.'
              : 'An unexpected error occurred. Reloading usually fixes it.'}
          </p>
          <button
            onClick={this.handleReload}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition"
            data-testid="chunk-error-reload-btn"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default ChunkErrorBoundary;
